import _ from 'lodash';
import {
  GetWorkerSkillEntriesResult,
  GetWorkerSkillEntrySignalsResult,
  WorkerProjectQualityResponse,
  BulkGetQualitySignalsResult,
  GetQualitySignalsByWorkerSkillEntryIdsAndDateRangeRequest,
} from '@scale/genai-data-service-client';
import apiFetch, { ApiFetchOptions } from '@scale/frontend-lib/api-fetch';
import {
  SetProjectTaxonomyParams,
  SetProjectTaxonomyResponse,
  InstructionsTaxonomyMismatchParams,
  InstructionsTaxonomyMismatchResponse,
} from '@scale/shared/types/endpoints/customer/selfserve/taxonomy';
import {
  GetProjectValidatorsResponse,
  SetProjectValidatorsParams,
} from '@scale/shared/types/endpoints/customer/selfserve/linters';

import {
  GetTemplateResponse,
  ValidateTemplateRequest,
  ValidateTemplateResponse,
  PushTemplateRequest,
  PushTemplateResponse,
} from '@scale/shared/taskTemplate/requestTypes';
import {
  SelfServeSupportedAttachmentTypes,
  InstructionCommentThreadStatus,
  CustomerReviewStatus,
  ReviewLevel,
} from '@scale/shared/defaults';
import {
  UpdateTrainingScenarioParams,
  RapidProjectUseCase,
  QueryItem,
} from '@scale/shared/selfServe/defaults';
import {
  UserSeenInstructionsParams,
  FetchInstructionsParams,
} from '@scale/shared/types/endpoints/customer/selfserve';
import { WorkerSkillsByIdsRequest, WorkerSkillsByIdsResponse } from '@scale/shared/selfServeRoutes';
import {
  BenchmarkResponse,
  CreateBenchmarkParams,
  DismissBenchmarkAlertParams,
  BenchmarkAlertParams,
  BenchmarkAlertResponse,
} from '@scale/shared/types/endpoints/customer/selfserve/benchmark';
import { RapidProjectTemplate } from 'dashboard/components/SelfServe/types';
import {
  TaskStatsByBatchResponse,
  TaskStatsByBatchParams,
} from '@scale/shared/types/endpoints/customer/stats/tasks/taskStatsByBatch';
import { CourseSection } from '@scale/shared/types/params/course';
import {
  TaskType,
  TaskPipelineName,
  IAnyTaskPermissionEntry,
  WorkerSkillEntriesbyWorkerAndWorkerSkillsRequest,
  WorkerSkillRequirementsByProjectRequest,
  WorkerSkillRequirementsByProjectResponse,
  GetQualityProfileConfigRequest,
  UpdateQualityProfileConfigRequest,
  GetQualitySignalsRequest,
  GetWorkerProjectBmStatsRequest,
  GetWorkerProjectBmStatsResponse,
} from '@scale/shared-types';
import {
  UpdateEndpointRequest,
  CreateEndpointRequest,
  SignedUploadRequest,
  SignedUploadResponse,
  SubmitTaskRequest,
  SubmitTaskResponse,
  GetTaskResponse,
  GetProjectConfigResponse,
  CreateProjectConfigRequest,
} from '@scale/shared/selfServe/scaleTrain';
import { CustomerLikedFeedbackParams } from '@scale/shared/types/endpoints/customer/selfserve/calibration';
import {
  RequestSuccessWithMessage,
  PhoneVerificationParams,
  ValidatePhoneVerificationParams,
  DismissAnnouncementParams,
  MarkNotificationsReadParams,
  MarkNucleusNotificationsReadParams,
  CheckShouldEnterOnboardingTourParams,
  ExitOnboardingTourParams,
  CheckShouldEnterOnboardingTourResponse,
} from '@scale/shared/types/endpoints/customer/selfserve/index';
import {
  CreateInstructionBatchParams,
  CreateRegularBatchParams,
} from '@scale/shared/types/endpoints/customer/selfserve/batch';
import { PresignedUploadResponse } from '@scale/shared/types/endpoints/customer/selfserve/upload';
import {
  GetCompletionsRequest,
  GetCompletionsResponse,
  FieldValidationConfigResponse,
  FieldValidationRequest,
  FieldValidationResponse,
  IRlhfPromptValidatorConfig,
  PRPPromptOptionsResponse,
  ValidatePromptRequest,
  ValidatePromptResponse,
} from '@scale/shared/types/rlhf';
import { AuditSessionStats } from '@scale/shared/types/endpoints/customer/selfserve/audit';
import { TasksResults } from '@scale/shared/types/api/internal/customerTasks';
import { TaskerQuizEntryProps } from '@scale/shared/projectInstruction/types';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { AVTaxonomyConfigDTO } from '@scale/shared/taxonomyService/dto/AVTaxonomyConfigDTO';
import { AVInstructionsDTO } from '@scale/shared/taxonomyService/dto/AVInstructionsDTO';
import { getInstructionsForAssignment } from 'react-scale/dashboard.scale.com/components/AVTaxonomy/apiClients/TaxonomyApiClient';
import {
  FetchFeedbackForRunResponseDTO,
  ModelQualitativeFeedbackSurveyConfig,
  ModelQualitativeFeedbackSurveyDTO,
  ModelEvalRunInfoDTO,
  ModelEvalRunMetadata,
} from '@scale/shared/modelEval';
import {
  ModelEvalEndpointDTO,
  ProjectModelConfigHistoryDTO,
  EvalCustomerListRunScores,
} from '@scale/customer-app-shared';

// posts
export async function customerLikedFeedback(params: CustomerLikedFeedbackParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<RequestSuccessWithMessage>(
    null,
    `internal/self_serve/tasks/like_feedback`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function requestPhoneVerification(params: PhoneVerificationParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<RequestSuccessWithMessage>(
    null,
    `internal/self_serve/request_phone_verification`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function validatePhoneVerification(params: ValidatePhoneVerificationParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<RequestSuccessWithMessage>(
    null,
    `internal/self_serve/validate_phone_verification`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function setNotificationsRead(params: MarkNotificationsReadParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<{}>(null, `internal/self_serve/notifications/mark_read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function dismissAnnouncement(params: DismissAnnouncementParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<{}>(null, `internal/self_serve/announcement/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function checkShouldEnterOnboardingTour(params: CheckShouldEnterOnboardingTourParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<CheckShouldEnterOnboardingTourResponse>(
    null,
    `internal/self_serve/event/check_should_enter_onboarding_tour`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function exitOnboardingTour(params: ExitOnboardingTourParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<{}>(null, `internal/self_serve/event/exit_onboarding_tour`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function enterOnboardingTour(params: ExitOnboardingTourParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<{}>(null, `internal/self_serve/event/enter_onboarding_tour`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setNucleusNotificationsRead(params: MarkNucleusNotificationsReadParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<{}>(null, `internal/self_serve/notifications/mark_read_nucleus`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function inheritWorkers(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/inherit_workers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setLidarPipeline(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/set_lidar_pipeline`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setTextCollectionUseTemplate(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/set_text_collection_use_template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function fetchProjectAssetById(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets_by_id`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function dismissBenchmarkAlert(params: DismissBenchmarkAlertParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<RequestSuccessWithMessage>(
    null,
    `internal/self_serve/benchmarks/alerts/dismiss`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function setProjectTaxonomy(params: SetProjectTaxonomyParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<SetProjectTaxonomyResponse>(null, `internal/self_serve/taxonomy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setDraftProjectTaxonomy(params: SetProjectTaxonomyParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<SetProjectTaxonomyResponse>(
    null,
    `internal/self_serve/project/${params.project}/taxonomy/draft`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function initializeNucleusDataset(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/initialize_nucleus_dataset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function initializeNucleusStaticAssetsDataset(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/initialize_nucleus_static_assets_dataset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function createInstructionBatch(
  params: CreateInstructionBatchParams,
  options?: ApiFetchOptions,
) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<RequestSuccessWithMessage>(
    null,
    `internal/self_serve/create_instruction_batch`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...options,
    },
  );
}

export async function createRegularBatch(
  params: CreateRegularBatchParams,
  options?: ApiFetchOptions,
) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<RequestSuccessWithMessage>(
    null,
    `internal/self_serve/create_regular_batch`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...options,
    },
  );
}

export async function setConfigValues(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setConfigValuesByPath(params: {
  project: string | undefined;
  changesJson: string;
}) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_configs_by_path`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function dismissCustomGrading(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/dismiss_custom_grading`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function createOrEditConcept(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/create_or_edit_concept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deleteConcept(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/delete_concept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setGraderConfig(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_graders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setGraderConfigByExample(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/set_graders_by_example`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setEdgeCaseAlerts(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_edge_case`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setCommonErrors(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_common_errors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function useAsGoodInstructionsExample(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/good_instructions_example`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function useAsCommonErrorExample(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/common_error_example`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function useAsCustomInstructionExample(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/custom_instruction_example`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// update benchmark/training task taxonomies to latest copy
export async function massCopyTaxonomyForExamples(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<RequestSuccessWithMessage>(
    null,
    `internal/self_serve/instructions/example/mismatch`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function updateExamplesWithNewTaxonomy(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/example/update_taxonomy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateTrainingTasksWithNewTaxonomy(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks/update_taxonomy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, forCourse: true }),
  });
}

export async function updateBenchmarksWithNewTaxonomy(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks/update_taxonomy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, forCourse: false }),
  });
}

export async function setLongHintsJSON(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_long_hints_json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setLongHints(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_long_hints`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setAutoRedos(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_auto_redos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setRapidSmartTraining(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/set_smart_training`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateInitialSetup(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/initial_setup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function pauseProject(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/pause_project`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function unPauseProject(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/unpause_project`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateBatchProps(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_batch_props`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function getBatchTimeBucket(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/batch_time_buckets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function adultContentToggle(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/tasker/adult_content_toggle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// update user info
export async function updateUserInfo(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_user_info`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// upload an user uploaded image for instructions
export async function uploadInstructionImage(params: $FIXME) {
  const formData = new FormData();
  formData.append('image', params.file);
  return await apiFetch(null, `internal/self_serve/instructions/upload`, {
    method: 'POST',
    body: formData,
  });
}

export async function setProjectInstructions(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setProjectInstructionsV2({ projectId, ...params }: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project/${projectId}/instruction`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setDraftProjectInstructions({ projectId, ...params }: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${projectId}/taxonomy/draft/instruction`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

// creates a task storing an instructions example
export async function setInstructionsExample(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/example`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// creates a task storing an instructions example
export async function setInstructionsStaticExample(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/example/static`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// update a task storing an instructions example
export async function updateInstructionsExample(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/example/update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function getInstructionDatasetId(params: $FIXME) {
  const { nucleusDatasetId } = params;
  return await apiFetch(null, `internal/self_serve/assets/instruction_asset_dataset`, {
    method: 'GET',
    query: { dataset: nucleusDatasetId as string },
  });
}

export async function getAmountOfAssets(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets/count`, { query });
}

export async function startCDSMultiUpload(params: $FIXME, options: ApiFetchOptions) {
  const { asset, project, size, mimeType, name, isStaticAsset } = params;
  const formData = new FormData();
  formData.append('asset', asset);
  formData.append('project', project);
  formData.append('size', size);
  formData.append('mimeType', mimeType);
  formData.append('name', name);
  formData.append('isStaticAsset', isStaticAsset);
  return await apiFetch(null, `internal/self_serve/assets/start_cds_multi_upload`, {
    method: 'POST',
    body: formData,
    ...options,
  });
}

export async function completeCDSMultiUpload(params: $FIXME, options: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/complete_cds_multi_upload`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

export async function retrieveCDSPartUrl(params: $FIXME, options: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/retrieve_cds_part_url`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

export async function createPresignedPostUrl(params: $FIXME, options: ApiFetchOptions) {
  const { asset, project } = params;
  const formData = new FormData();
  formData.append('asset', asset);
  formData.append('project', project);
  return await apiFetch<PresignedUploadResponse>(
    null,
    `internal/self_serve/assets/presigned_upload`,
    {
      method: 'POST',
      body: formData,
      ...options,
    },
  );
}

export async function uploadAssetFromPresignedUrl(params: $FIXME, options: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/upload_from_presigned_url`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

export async function uploadAssetFromComputer(params: $FIXME, options: ApiFetchOptions) {
  const { asset, project, uploadGroupId, isStaticAsset, publiclyViewable, skipCopyingToNucleus } =
    params;
  const formData = new FormData();
  formData.append('asset', asset);
  formData.append('project', project);
  formData.append('uploadGroupId', uploadGroupId);
  formData.append('isStaticAsset', isStaticAsset);
  formData.append('publiclyViewable', publiclyViewable);
  formData.append('skipCopyingToNucleus', skipCopyingToNucleus);
  return await apiFetch(null, `internal/self_serve/assets/upload_from_computer`, {
    method: 'POST',
    body: formData,
    ...options,
  });
}

// upload assets using a cron
export async function uploadAssetFromCSVAsync(params: $FIXME, options: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/upload_from_csv_async`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

// upload assets not using a cron
export async function uploadAssetFromCSV(params: $FIXME, options: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/upload_from_csv`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

// upload assets using a cron
export async function uploadAssetTemplate(params: $FIXME, options?: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/template`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

// add projectId to template's taskPreviewAsset/UploadedAsset
export async function addProjectToTemplateAsset(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets/templateAsset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function useSampleDataset(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets/use_sample_dataset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function importNucleusDataset(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets/import_nucleus_dataset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function uploadAssetsFromS3(params: $FIXME, options: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/upload_from_s3`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

export async function uploadAssetsFromGCP(params: $FIXME, options: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/upload_from_gcp`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

export async function uploadAssetsFromAzure(params: $FIXME, options: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/upload_from_azure`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

export async function uploadAssetsFromPreviousProject(params: $FIXME, options: ApiFetchOptions) {
  return await apiFetch(null, `internal/self_serve/assets/upload_from_previous_project`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

export async function deleteAllAssets(params: $FIXME, options: ApiFetchOptions = {}) {
  return await apiFetch(null, `internal/self_serve/assets/delete_all_assets`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

export async function deleteAssets(params: $FIXME, options: ApiFetchOptions = {}) {
  return await apiFetch(null, `internal/self_serve/assets/delete_assets`, {
    method: 'POST',
    body: params,
    ...options,
  });
}

export async function toggleUploadedAssetForLabeledExample(params: $FIXME) {
  return await apiFetch(null, `internal/self_serve/assets/toggle_asset_for_labeled_examples`, {
    method: 'POST',
    body: params,
  });
}

export async function updateUploadedAssetText(params: $FIXME) {
  return await apiFetch(null, `internal/self_serve/assets/update_text_asset`, {
    method: 'POST',
    body: params,
  });
}

// creates a benchmark in the backend
export async function createBenchmark(params: CreateBenchmarkParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<RequestSuccessWithMessage>(null, `internal/self_serve/create_benchmark`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// retires a benchmark in the backend
export async function retireBenchmark(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/retire_benchmark`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// re-enables a retired benchmark in the backend
export async function reenableBenchmark(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/reenable_benchmark`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// converts a benchmark in the backend
export async function convertBenchmark(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/convert_benchmark`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// accepts/rejects a suggested benchmark in the backend
export async function convertSuggestedBenchmark(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/convert_suggested_benchmark`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// retires a group of benchmarks in the backend
export async function massRetireBenchmarks(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/mass_retire_benchmarks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// returns number of benchmarks that would be affected by mass retire
export async function massRetireBenchmarksPreview(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/mass_retire_benchmarks_preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function setNeedToBeReviewed(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/set_need_to_be_reviewed`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// converts a batch and launches the project
export async function convertBatch(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/convert_batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// cancel a batch
export async function cancelBatch(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/cancel_batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// finalize a batch
export async function finalizeBatch(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/finalize_batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// update response on a training scenario
export async function updateTrainingScenario(params: UpdateTrainingScenarioParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_training_scenario`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// update benchmark/training task taxonomies to latest copy
export async function massCopyTaxonomy(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/mass_copy_taxonomy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function edgeCaseAction(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/edge_case/action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function validateResponse(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks/validate_response`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateBenchmarkFrequency(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks/update_benchmark_frequency`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateBenchmarkRecycling(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks/update_benchmark_recycling`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateBenchmarkChildScenarios(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks/update_benchmark_child_scenarios`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function incrementProjectTemplateUsage(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project_template/increment_usage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateCustomerAttachmentHipaaCoverage(params: {
  isAttachmentCoveredByHipaa: boolean;
}) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<{}>(null, `internal/self_serve/hipaa_coverage_attachment_customer_level`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateProjectAttachmentHipaaCoverage(params: {
  project: string;
  isAttachmentCoveredByHipaa: boolean;
}) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch<{}>(null, `internal/self_serve/hipaa_coverage_attachment_project_level`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function redeemPaymentCredit(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/redeem_payment_credit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function requestProjectTemplate(params: { asset: File | null; message: string }) {
  const formData = new FormData();
  formData.append('asset', params.asset || '');
  formData.append('message', params.message);
  return await apiFetch(null, `internal/self_serve/request_project_template`, {
    method: 'POST',
    body: formData,
  });
}

// gets
export async function fetchReferralLink(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/referral_link`, { query });
}

export async function fetchNotifications(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/notifications`, { query });
}

export async function fetchCurrentAnnouncements() {
  return await apiFetch(null, `internal/self_serve/announcement`);
}

export async function fetchNucleusNotifications(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/nucleusNotifications`, { query });
}

export async function fetchProjectNotifications({ projectId, ...params }: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/notifications/${projectId}`, { query });
}

export async function fetchProjectTaxonomy(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/taxonomy`, { query });
}

export async function fetchProjectTaxonomyVersion(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/taxonomy/${params.version}`,
    {
      query,
    },
  );
}

export async function fetchProjectTaxonomyLatestVersion(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project/${params.project}/taxonomy/latest`, {
    query,
  });
}

export async function fetchDraftProjectTaxonomy(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project/${params.project}/taxonomy/draft`, {
    query,
  });
}

export async function fetchLatestProjectTaxonomy(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project/${params.project}/taxonomy/latest`, {
    query,
  });
}

export async function fetchCommonAnswers(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/answers`, { query });
}

export async function getLongHintsJSON(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/get_long_hints_json`, { query });
}

export async function fetchNumProjects() {
  return await apiFetch(null, `internal/self_serve/projects`, {});
}

export async function fetchProjectAssets(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets`, { query });
}

export async function fetchProjectAssetsLite(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets_lite`, { query });
}

export async function fetchCursorPaginatedProjectAssets(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets/cursor`, { query });
}

export async function fetchElasticProjectAssets(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets/elastic`, {
    method: 'POST',
    body,
  });
}

export async function fetchConcepts(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/all_concepts`, { query });
}

export async function fetchActiveBenchmarks(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<BenchmarkResponse>(null, `internal/self_serve/active_benchmarks`, {
    query,
  });
}

export async function fetchNumDisabledBenchmarks(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/disabled_benchmarks`, { query });
}

export async function fetchSuggestedBenchmarks(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/suggested_benchmarks`, { query });
}

export async function fetchBenchmarkById(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarkById`, { query });
}

export async function fetchBenchmarks(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks`, { query });
}

export async function fetchGraderBenchmarks(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/grader_benchmarks`, { query });
}

export async function fetchBenchmarkAlerts(params: BenchmarkAlertParams) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<BenchmarkAlertResponse>(null, `internal/self_serve/benchmarks/alerts`, {
    query,
  });
}

export async function fetchTaskForBenchmark(params: $FIXME) {
  const query: $FIXME = _.omitBy(params, _.isNil);
  query.lean = true;
  return await apiFetch(null, `internal/self_serve/benchmarks/tasks`, { query });
}

export async function fetchProjectInstructions(params: FetchInstructionsParams) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions`, { query });
}

export async function fetchProjectInstructionsV2(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project/${params.projectId}/instruction`, {
    query,
  });
}

export async function fetchDraftProjectInstructionsV2(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.projectId}/taxonomy/draft/instruction`,
    {
      query,
    },
  );
}

export async function fetchLatestProjectInstructions(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.projectId}/taxonomy/latest/instruction`,
    {
      query,
    },
  );
}

export async function fetchProjectInstructionVersion(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/taxonomy/${params.version}/instruction`,
    {
      query,
    },
  );
}

export async function getProjectInstructionStatus(projectId: string, assignmentId?: string) {
  if (assignmentId) {
    const taxonomyServiceInstructions: AVInstructionsDTO | null =
      await getInstructionsForAssignment(assignmentId);
    if (taxonomyServiceInstructions) {
      return {
        ok: true,
        instruction: taxonomyServiceInstructions.instruction,
        firstSeenProjectInstruction: false,
        hasSensitiveContent: taxonomyServiceInstructions.instruction.hasSensitiveContent,
        param_chunks: false,
        title: taxonomyServiceInstructions.instruction.title,
        shouldUseNGI: true,
        diffInstructions: taxonomyServiceInstructions.diffInstructions,
      };
    }
  }
  const query = _.omitBy({ assignmentId }, _.isNil);
  return await apiFetch(null, `internal/self_serve/project/${projectId}/instruction/status`, {
    method: 'GET',
    query,
  });
}

export async function submitQuiz(submittedQuiz: TaskerQuizEntryProps) {
  return await apiFetch(null, `internal/self_serve/projectInstruction/quiz/submit`, {
    method: 'POST',
    body: {
      submittedQuiz,
    },
  });
}

export async function updateQuiz(submittedQuiz: TaskerQuizEntryProps) {
  return await apiFetch(null, `internal/self_serve/projectInstruction/quiz/update`, {
    method: 'POST',
    body: {
      submittedQuiz,
    },
  });
}

export async function getNextQuiz(taxonomyVersion: string) {
  return await apiFetch(null, `internal/self_serve/projectInstruction/quiz/${taxonomyVersion}`, {
    method: 'GET',
  });
}

export async function switchInstructionQuiz(projectId: string, enableQuiz: boolean) {
  return await apiFetch(null, `internal/self_serve/project/${projectId}/instruction/enable_quiz`, {
    method: 'POST',
    body: {
      enableQuiz,
    },
  });
}

export async function getInstructionQuizFlag(projectId: string) {
  return await apiFetch(null, `internal/self_serve/project/${projectId}/instruction/enable_quiz`, {
    method: 'GET',
  });
}

export async function fetchUserHasSeenInstructions(params: UserSeenInstructionsParams) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/user_has_seen`, { query });
}

export async function setUserHasSeenInstructions(params: UserSeenInstructionsParams) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/set_user_has_seen`, { query });
}

// get info about a task storing an instructions example (so it can be rendered)
export async function fetchInstructionExample(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/example`, { query });
}

export async function fetchTasksRemainingInBatch(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/label/tasks_remaining`, { query });
}

export async function fetchInstructionPageFeedback(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instruction_page_feedback_customer`, { query });
}

export async function fetchInstructionExampleTaxonomyMismatches(
  params: InstructionsTaxonomyMismatchParams,
) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<InstructionsTaxonomyMismatchResponse>(
    null,
    `internal/self_serve/instructions/example/mismatch`,
    { query },
  );
}

// fetch a signed S3 URL
export async function fetchInstructionAssetS3URL(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instructions/image`, { query });
}

export async function fetchAssetS3URL(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/upload/asset`, { query });
}

export async function fetchAssetPages(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/upload/asset/pages`, { query });
}

export async function fetchAssetToTemplateAttachment(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/upload/asset/to_template_attachment`, { query });
}

export async function fetchConfigValues(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/configs`, { query });
}

export async function fetchProjectNGIConfig(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project_ngi_config`, { query });
}

export async function fetchProjectAuditConfig(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project_audit_config`, { query });
}

export async function fetchAVTaxonomyConfig(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return (await apiFetch(null, `internal/self_serve/avtaxonomy_config`, {
    query,
  })) as AVTaxonomyConfigDTO;
}

export async function fetchConfigValuesByPath(params: {
  project: string | undefined;
  configPaths: string[];
}) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/configs_by_path`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function fetchUserIntegrationInfo(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/integration_info`, { query });
}

export async function fetchGraderConfig(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/graders`, { query });
}

export async function rerunDefaultGraders(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/rerun_default_graders`, { query });
}

export async function fetchInfo(params: $FIXME) {
  const { project } = params;
  return await apiFetch(null, `internal/self_serve/info/${project}`);
}

export async function fetchState(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/current_state`, { query });
}

export async function fetchProject(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project`, { query });
}

export async function fetchMetrics(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/metrics`, { query });
}

export async function fetchMongoMetrics(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/mongo_metrics`, { query });
}

export async function fetchInstructionFeedback(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/instruction_feedback_customer`, { query });
}

export async function fetchTaskBatches(params: { project: string; limit?: number; skip?: number }) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<{ batches: any[] }>(null, `internal/self_serve/taskbatches`, { query });
}

export async function fetchHasLiveBatch(params: { project: string }) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<{ hasLiveBatch: boolean }>(null, `internal/self_serve/has_live_batch`, {
    query,
  });
}
export async function fetchBatchesFromName(batchName: string) {
  return await apiFetch(null, `internal/self_serve/batches/${batchName}`);
}

export async function fetchTaskStatsByBatch(
  filters: TaskStatsByBatchParams,
): Promise<TaskStatsByBatchResponse> {
  const query = _.omitBy(filters, _.isNil);

  return await apiFetch(null, `internal/self_serve/stats/tasks/by-batch`, { query });
}

export async function fetchTaskStatsForBatch(batchId: string) {
  return await apiFetch(null, `internal/self_serve/stats/tasks/for-batch`, { query: { batchId } });
}

export async function fetchBatchTaskCreationData(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/fetch_batch_task_creation_data`, { query });
}

export async function fetchEdgeCaseAlerts(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/edge_case/list`, { query });
}

export async function checkBatchProperties(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/check_batch_properties`, { query });
}

export async function fetchNeedToBeReviewed(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/get_need_to_be_reviewed`, { query });
}

export async function fetchBillingSpecs(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/billing_specs`, { query });
}

export async function fetchBenchmarkFrequency(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks/get_benchmark_frequency`, {
    query,
  });
}

export async function fetchBenchmarkRecycling(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks/get_benchmark_recycling`, {
    query,
  });
}

export async function fetchExistedBenchmarksFromTask(params: { taskId: string }) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<{ existingBenchmarksIds: string[] }>(
    null,
    `internal/self_serve/benchmarks/existed_benchmarks_from_task`,
    {
      query,
    },
  );
}

export async function fetchProjectAttachmentHipaaCoverage(params: { project?: string }) {
  return await apiFetch<{ isAttachmentCoveredByHipaa: boolean }>(
    null,
    `internal/self_serve/hipaa_coverage_attachment`,
    {
      query: params,
    },
  );
}

export async function fetchAdeptBatchResult(params: { batchId: string }) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/adept_batch_result`, { query });
}

export async function fetchProjectTemplatePreviews() {
  return await apiFetch<{ projectTemplates: RapidProjectTemplate[] }>(
    null,
    `internal/self_serve/project_template/previews`,
    {},
  );
}

export async function fetchSampleDatasets(params: {
  assetType?: SelfServeSupportedAttachmentTypes;
}) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/assets/sample_datasets`, { query });
}

// puts
export async function validateParams(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/validate`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// task template api from frontend/corp/lib/api/taskTemplate.ts

export async function validateTaskTemplate(
  taskType: TaskType,
  params: unknown,
): Promise<ValidateTemplateResponse> {
  return await apiFetch<ValidateTemplateResponse, ValidateTemplateRequest>(
    null,
    `internal/self_serve/task_template/validate`,
    {
      method: 'POST',
      body: {
        // TODO: serialize once instead of twice
        paramsJson: JSON.stringify(params),
        type: taskType,
      },
    },
  );
}

export async function fetchTaskTemplate(projectId: string) {
  return await apiFetch<GetTemplateResponse>(
    null,
    `internal/self_serve/task_template/${projectId}`,
  );
}

export interface PartialTemplate {
  params: unknown;
  expectedVariables?: string[];
  templateVariables?: Record<string, string>;
}

export async function pushTaskTemplate(
  projectId: string,
  partialTemplate: PartialTemplate,
): Promise<PushTemplateResponse> {
  return await apiFetch<PushTemplateResponse, PushTemplateRequest>(
    null,
    `internal/self_serve/task_template/${projectId}`,
    {
      method: 'POST',
      body: {
        // TODO: serialize once instead of twice
        partialTemplateJson: JSON.stringify(partialTemplate),
      },
    },
  );
}

export async function resetCourseProgress(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/benchmarks/reset_course_progress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function getTrainModelRuns(projectId: string) {
  return await apiFetch(null, `internal/self_serve/models/${projectId}/runs`);
}

export async function getTrainModelSignedUrl(projectId: string, runName: string) {
  return await apiFetch(
    null,
    `internal/self_serve/models/${projectId}/run/${runName}/signed_model_url`,
  );
}

type AutoModelTrainingRequest = {
  batchIds: string[];
  devRun?: boolean;
};

export async function submitModelTrainingRequest(
  projectId: string,
  params: AutoModelTrainingRequest,
) {
  return await apiFetch<$FIXME, $FIXME>(
    null,
    `internal/self_serve/models/${projectId}/submit_new_training`,
    {
      method: 'POST',
      body: params,
    },
  );
}

export async function getEndpoints({ projectId }: { projectId: string }) {
  return await apiFetch(null, `internal/self_serve/models/${projectId}/endpoints`);
}

export async function submitEndpointCreationRequest({
  projectId,
  data,
}: {
  projectId: string;
  data: CreateEndpointRequest;
}) {
  return await apiFetch(null, `internal/self_serve/models/${projectId}/create_new_endpoints`, {
    method: 'POST',
    body: data,
  });
}

export async function submitEndpointUpdateRequest({
  projectId,
  data,
}: {
  projectId: string;
  data: UpdateEndpointRequest;
}) {
  return await apiFetch(null, `internal/self_serve/models/${projectId}/update_endpoint`, {
    method: 'PUT',
    body: data,
  });
}

export async function fetchCourses(projectId: string) {
  return await apiFetch(null, `internal/self_serve/courses/${projectId}`, {
    method: 'GET',
  });
}

export async function getUseCaseAndPipeline(projectId: string) {
  return await apiFetch(null, `internal/self_serve/get_use_case_and_pipeline/${projectId}`, {
    method: 'GET',
  });
}

export async function initializeUseCaseAndPipeline(params: {
  project: string;
  useCase: RapidProjectUseCase;
  pipeline: TaskPipelineName;
  taskType: TaskType;
}) {
  return await apiFetch(null, `internal/self_serve/initialize_use_case_and_pipeline`, {
    method: 'POST',
    body: params,
  });
}

export async function postImageUpload(
  params: SignedUploadRequest,
): Promise<{ data: SignedUploadResponse }> {
  return apiFetch(null, `internal/self_serve/models/playground/signed_upload`, {
    method: 'POST',
    body: params,
  });
}

export async function postSubmitTask(
  projectId: string,
  endpointId: string,
  params: SubmitTaskRequest,
): Promise<{ data: SubmitTaskResponse }> {
  return apiFetch(
    null,
    `internal/self_serve/models/${projectId}/endpoints/${endpointId}/tasks/submit_task`,
    {
      method: 'POST',
      body: params,
    },
  );
}

export async function getTask(
  projectId: string,
  endpointId: string,
  taskId: string,
): Promise<{ data: GetTaskResponse }> {
  return apiFetch(
    null,
    `internal/self_serve/models/${projectId}/endpoints/${endpointId}/tasks/${taskId}`,
    {
      method: 'GET',
    },
  );
}

export async function createCourse(projectId: string) {
  return await apiFetch(null, `internal/self_serve/courses/${projectId}/new`, {
    method: 'POST',
  });
}

export async function createFeedbackCourse(
  projectId: string,
  announcements: string[],
  taskId: string,
) {
  return await apiFetch(null, `internal/self_serve/courses/${projectId}/new_feedback_course`, {
    method: 'POST',
    body: {
      announcements,
      taskId,
    },
  });
}

export async function updateCourse(
  courseId: string,
  disabled?: boolean,
  available?: boolean,
  title?: string,
  sections?: CourseSection[],
  projectId?: string,
) {
  return await apiFetch(null, `internal/self_serve/courses/${courseId}`, {
    method: 'POST',
    body: {
      updates: {
        disabled,
        available,
        title,
        sections,
      },
      projectId,
    },
  });
}

export async function updateCourseOrder(projectId: string, partialCourseOrder: string[]) {
  return await apiFetch(null, `internal/self_serve/courses/${projectId}/reorder`, {
    method: 'POST',
    body: {
      partialCourseOrder,
    },
  });
}

export async function getProjectConfig(
  projectId: string,
): Promise<{ data: GetProjectConfigResponse }> {
  return await apiFetch(null, `internal/self_serve/models/${projectId}/config`, {
    method: 'GET',
  });
}

export async function postProjectConfig(projectId: string, config: CreateProjectConfigRequest) {
  return await apiFetch(null, `internal/self_serve/models/${projectId}/config`, {
    method: 'POST',
    body: config,
  });
}

export async function fetchProjectValidators(params: {
  project: string;
  isInternal?: boolean;
}): Promise<GetProjectValidatorsResponse> {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/validators`, { method: 'GET', query });
}

export async function setProjectValidators(params: SetProjectValidatorsParams) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/validators`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function getSavedPromptValidators() {
  return await apiFetch(null, `internal/self_serve/validators/saved_prompt_validators`, {
    method: 'GET',
  });
}

export async function savePromptValidator(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/validators/save_prompt_validator`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function disableSavedPrompt(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/validators/disable_saved_prompt`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function getProjectPromptValidators(params: { project: string }) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/validators/project_prompt_validators`, {
    method: 'GET',
    query,
  });
}

export async function getProjectTaxonomyForLinters(params: $FIXME) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/validators/get_project_taxonomy_for_linters`, {
    method: 'GET',
    query,
  });
}

export async function getLinterMetrics(params: { projectId: string; linterNames: string[] }) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/validators/get_linter_metrics`, {
    method: 'GET',
    query,
  });
}

export async function getBackdatedLinterMetrics(params: {
  project: string;
  linterNames: string;
  level?: string[];
  status?: string;
  taskIds?: string[];
}) {
  return await apiFetch(null, `internal/self_serve/validators/get_backdated_linter_metrics`, {
    method: 'GET',
    query: params,
  });
}

export async function saveProjectPromptValidators(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/validators/project_save_prompt_validators`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function publishDraftTaxonomy(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/taxonomy/draft/publish`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function deleteDraftTaxonomy(params: $FIXME) {
  return await apiFetch(null, `internal/self_serve/project/${params.project}/taxonomy/draft`, {
    method: 'DELETE',
  });
}

export async function createAuditSession(params: {
  project: string;
  numTasks: number;
  filters: {
    filterSessionUsedTasks: boolean;
    filterAnyAuditedTask: boolean;
    selectedBatchIds: string[];
    dateFrom?: Date;
    dateTo?: Date;
  };
}) {
  return await apiFetch<any>(null, `internal/self_serve/audit/audit_sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function renameAuditSession(params: { auditSessionId: string; name: string }) {
  return await apiFetch<TasksResults>(null, `internal/self_serve/audit/audit_sessions/rename`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function setIsArchivedAuditSession(params: {
  auditSessionId: string;
  isArchived: boolean;
}) {
  return await apiFetch<TasksResults>(
    null,
    `internal/self_serve/audit/audit_sessions/set_is_archived`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );
}

export async function fetchAuditSessions(params: {
  project: string;
  limit?: number;
  offset?: number;
}) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<{ auditSessions: AuditSessionStats[]; totalSessions: number }>(
    null,
    `internal/self_serve/audit/audit_sessions`,
    { method: 'GET', query },
  );
}

export async function fetchAuditSessionTasks(params: {
  auditSessionId: string;
  limit?: number;
  offset?: number;
}) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<TasksResults>(null, `internal/self_serve/audit/audit_session_tasks`, {
    method: 'GET',
    query,
  });
}

export async function fetchCustomerChatAudit(queryParams: { taskId: string; batchId?: string }) {
  return await apiFetch(null, `internal/self_serve/audit/customerChatAudits`, {
    method: 'GET',
    query: queryParams,
  });
}

export interface CustomerChatAuditUpsert {
  taskId: string;
  taskType: TaskType;
  runId?: string;
  batchId?: string;
  updatedResponse: Object;
  grade: string | null;
  overallFeedback: string;
  status: CustomerReviewStatus;
}

export async function upsertCustomerChatAudit(params: CustomerChatAuditUpsert) {
  return await apiFetch(null, `internal/self_serve/audit/customerChatAudits`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function deleteCustomerChatAudit(params: {
  taskId: string;
  runId?: string;
  batchId?: string;
  response: Object;
}) {
  return await apiFetch(null, `internal/self_serve/audit/customerChatAudits`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}
export async function fetchCustomerComments(queryParams: { auditId: string }) {
  return await apiFetch(null, `internal/self_serve/audit/comments`, {
    method: 'GET',
    query: queryParams,
  });
}

export async function removeCustomerComment(params: { auditId: string; path: string }) {
  return await apiFetch(null, `internal/self_serve/audit/comment`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function addCustomerComment(params: {
  auditId: string;
  path: string;
  comment: string;
}) {
  return await apiFetch(null, `internal/self_serve/audit/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function fetchVersions(params: $FIXME) {
  return await apiFetch(null, `internal/self_serve/project/${params.project}/taxonomy`, {
    method: 'GET',
  });
}

export async function fetchVersionTaskCounts(params: $FIXME) {
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/taxonomy/${params.version}/task_counts`,
    {
      method: 'GET',
    },
  );
}

export async function compareVersions(params: {
  project: string;
  versionFrom: string;
  versionTo: string;
}) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/project/${params.project}/taxonomy/compare`, {
    query,
  });
}

export async function updateVersion(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/taxonomy/${params.version}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function dolphinGenerateResponse(params: {
  assignmentId?: string;
  prompt: string;
  language?: string;
}) {
  return await apiFetch<{ [fieldId: string]: string }>(
    null,
    `internal/self_serve/dolphin/generateResponses`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );
}

export async function dolphinGeneratePrompt() {
  return await apiFetch<{ prompt: string }>(null, `internal/self_serve/dolphin/generatePrompt`);
}

export async function fetchResponsesFromChatEndpoint(params: {
  endpointUrl: string;
  getCompletionsRequest: GetCompletionsRequest;
  taskCustomer: string | undefined;
  assignmentId?: string;
}) {
  return await apiFetch<GetCompletionsResponse>(
    null,
    `internal/self_serve/rlhf/fetchResponsesFromChatEndpoint`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );
}

export async function getModelConfigurationsByIdAndVersion(modelId: string, version: number) {
  return await apiFetch<ModelEvalEndpointDTO>(
    null,
    `internal/self_serve/rlhf/getModelConfigurationByIdAndVersion`,
    {
      method: 'GET',
      query: { modelId, version },
    },
  );
}

export async function getDraftProjectModelConfigurationFromHistory(projectId: string) {
  return await apiFetch<ProjectModelConfigHistoryDTO>(
    null,
    `internal/self_serve/rlhf/getDraftProjectModelConfiguration`,
    {
      method: 'GET',
      query: { projectId },
    },
  );
}

export async function getActiveProjectModelConfiguration(projectId: string) {
  return await apiFetch<{
    projectModelConfiguration: ProjectModelConfigHistoryDTO['projectConfigurationMapping'];
  }>(null, `internal/self_serve/rlhf/getLatestPopulatedProjectModelConfig`, {
    method: 'GET',
    query: { projectId, includeDraft: false },
  });
}

export async function fetchStreamingModelCheckpoint(params: {
  taskCustomer?: string;
  requestId: string;
}) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<string | null>(null, `internal/self_serve/rlhf/streamingModelCheckpoint`, {
    query,
  });
}

export async function updateStreamingModelCheckpoint(params: {
  taskCustomer?: string;
  requestId: string;
  data: string;
}) {
  return await apiFetch(null, `internal/self_serve/rlhf/streamingModelCheckpoint`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function fetchAutoSpec(params: { payload: any; taskCustomer: string | undefined }) {
  return await apiFetch<any>(null, `internal/self_serve/rlhf/fetchAutoSpec`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function fetchAutoCritique(params: {
  payload: any;
  taskCustomer: string | undefined;
}) {
  return await apiFetch<any>(null, `internal/self_serve/rlhf/fetchAutoCritique`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function fetchAiAssistantPrompts(projectId: string) {
  return await apiFetch<any>(null, `internal/self_serve/rlhf/aiAssistantPrompts`, {
    method: 'GET',
    query: { projectId },
  });
}

export async function fetchRlhfBlacklistWords() {
  return await apiFetch(null, `internal/self_serve/rlhf/getBlacklistWords`);
}

export async function fetchJWTForStreaming() {
  return await apiFetch(null, `internal/self_serve/rlhf/getJWTForStreaming`);
}

export async function askGPT(params: { promptSystem: string; promptUser: string; options: any }) {
  return await apiFetch(null, `internal/self_serve/rlhf/askGPT`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function validatePrompt(params: ValidatePromptRequest) {
  return await apiFetch<ValidatePromptResponse>(null, `internal/self_serve/rlhf/validatePrompt`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function validateReferenceText(params: {
  promptText: string;
  projectId: string;
  chatBoxText: string;
  assignmentId?: string;
}) {
  return await apiFetch<{ errors: string[]; warnings: string[] }>(
    null,
    `internal/self_serve/rlhf/validateReferenceText`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );
}

export async function sendBackToBase(params: {
  projectId: string;
  assignmentId: string | undefined;
  enableRlhfStdTaskFinalFixSBQ: boolean;
}) {
  return await apiFetch(null, `internal/self_serve/rlhf/sendBackToBase`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function testPostProcessConfig(params: $FIXME) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/rlhf/testPostProcessingPromptConfig`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function testPromptConfig(params: $FIXME) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/rlhf/testPromptConfig`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function fieldValidation(params: FieldValidationRequest) {
  return await apiFetch<FieldValidationResponse>(null, `internal/self_serve/rlhf/fieldValidation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function fetchFieldValidationConfig(params: { projectId: string }) {
  return await apiFetch<FieldValidationConfigResponse>(
    null,
    `internal/self_serve/rlhf/fieldValidationConfig`,
    {
      method: 'GET',
      query: params,
    },
  );
}

export async function fetchPRPPromptOptions(params: { projectId: string }) {
  return await apiFetch<PRPPromptOptionsResponse>(
    null,
    `internal/self_serve/rlhf/fetchPRPPromptOptions`,
    {
      method: 'GET',
      query: params,
    },
  );
}

export async function getWorkerProjectQuality(params: { workerId: string }) {
  return await apiFetch<$FIXME>(
    null,
    `internal/self_serve/qualityProfile/getWorkerProjectQuality`,
    {
      method: 'GET',
      query: params,
    },
  );
}

export async function getWorkerProjectQualityListByProject(params: { projectId: string }) {
  return await apiFetch<{ result?: WorkerProjectQualityResponse[]; error?: string }>(
    null,
    `internal/self_serve/qualityProfile/getWorkerProjectQualityListByProject`,
    {
      method: 'GET',
      query: params,
    },
  );
}

export async function getWorkerProjectQualitySample(params: {
  projectId: string;
  workerIds: string[];
  minDate: string;
}) {
  return await apiFetch<$FIXME>(
    null,
    `internal/self_serve/qualityProfile/getWorkerProjectQualitySample`,
    {
      method: 'POST',
      body: params,
    },
  );
}

type WorkerTagMappingsResponse = {
  result: Record<string, string[]>;
  error?: string;
};

export async function getWorkerTagMappings(params: { workerIds: string[]; tags: string[] }) {
  return await apiFetch<WorkerTagMappingsResponse>(
    null,
    `internal/self_serve/qualityProfile/getWorkerTagMappings`,
    {
      method: 'POST',
      body: params,
    },
  );
}

export async function getBenchmarkStats(params: GetWorkerProjectBmStatsRequest) {
  return await apiFetch<GetWorkerProjectBmStatsResponse>(
    null,
    `internal/self_serve/qualityProfile/getWorkerProjectBmStats`,
    {
      method: 'POST',
      body: params,
    },
  );
}

export async function getWorkerBenchmarkStats(params: {
  projectId: string;
  workerId: string;
  minDate: string;
  includeDisabledBms: boolean;
  includeSkippedAttempts: boolean;
}) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/qualityProfile/getWorkerBmStats`, {
    method: 'POST',
    body: params,
  });
}

export async function getWorkerAutoDisables(params: { projectId: string; workerId: string }) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/qualityProfile/getWorkerAutoDisables`, {
    method: 'POST',
    body: params,
  });
}

export async function getFraudTaskers(params: { projectId: string }) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/qualityProfile/getFraudTaskers`, {
    method: 'POST',
    body: params,
  });
}

export async function getFraudSignals(params: {
  projectId: string;
  workerIds: string[];
  reviewLevel: ReviewLevel;
  minDate: string;
  includeDisabledBms: boolean;
}) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/qualityProfile/getTaskerFraudSignals`, {
    method: 'POST',
    body: params,
  });
}

export async function getDisableAccuracyThresholds(params: { projectId: string }) {
  return await apiFetch<$FIXME>(
    null,
    `internal/self_serve/qualityProfile/getDisableAccuracyThresholds`,
    {
      method: 'GET',
      query: params,
    },
  );
}

export async function getAuditBmBatch(params: {
  workerId: string;
  projectId: string;
  minDate: string;
}) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/qualityProfile/getAuditBmBatch`, {
    method: 'GET',
    query: params,
  });
}

export async function getQualitySignalsByProjectId(params: { projectId: string }) {
  return await apiFetch<{
    result?: $FIXME;
    error?: string;
  }>(null, `internal/self_serve/qualityProfile/getQualitySignalsByProjectId`, {
    method: 'GET',
    query: params,
  });
}

export async function getWorkerSkillById(params: $FIXME) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/qualityProfile/getWorkerSkillById`, {
    method: 'GET',
    query: params,
  });
}

export async function getWorkerSkillsByIds(params: WorkerSkillsByIdsRequest) {
  return await apiFetch<WorkerSkillsByIdsResponse>(
    null,
    `internal/self_serve/qualityProfile/getWorkerSkillsByIds`,
    {
      method: 'POST',
      body: params,
    },
  );
}

export async function getWorkerSkillEntriesByWorkerId(params: $FIXME) {
  return await apiFetch<$FIXME>(
    null,
    `internal/self_serve/qualityProfile/getWorkerSkillEntriesByWorkerId`,
    {
      method: 'GET',
      query: params,
    },
  );
}

export async function getWritingSamplesByWorkerId(params: $FIXME) {
  return await apiFetch<$FIXME>(
    null,
    `internal/self_serve/qualityProfile/getWritingSamplesByWorkerId`,
    {
      method: 'GET',
      query: params,
    },
  );
}

export async function getProjectQualityTaskData(params: $FIXME) {
  return await apiFetch<$FIXME>(
    null,
    `internal/self_serve/qualityProfile/getProjectQualityTaskData`,
    {
      method: 'GET',
      query: params,
    },
  );
}

export async function getWorkerSkillTaskData(params: $FIXME) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/qualityProfile/getWorkerSkillTaskData`, {
    method: 'GET',
    query: params,
  });
}

export async function getWorkerSkillRequirementsByProject(
  query: WorkerSkillRequirementsByProjectRequest,
) {
  return await apiFetch<WorkerSkillRequirementsByProjectResponse>(
    null,
    `internal/self_serve/qualityProfile/getWorkerSkillRequirementsByProject`,
    {
      method: 'GET',
      query,
    },
  );
}

export async function getWorkerSkillEntriesByWorkerAndWorkerSkills(
  body: WorkerSkillEntriesbyWorkerAndWorkerSkillsRequest,
) {
  return await apiFetch<{ result: GetWorkerSkillEntriesResult['workerSkillEntries'] }>(
    null,
    `internal/self_serve/qualityProfile/getWorkerSkillEntriesByWorkerAndWorkerSkills`,
    {
      method: 'POST',
      body,
    },
  );
}

export async function getWorkerSkillEntrySignalsByWorkerSkillEntries(
  body: GetQualitySignalsRequest,
) {
  return await apiFetch<{ result: GetWorkerSkillEntrySignalsResult['workerSkillEntrySignals'] }>(
    null,
    `internal/self_serve/qualityProfile/getWorkerSkillEntrySignalsByWorkerSkillEntries`,
    {
      method: 'POST',
      body,
    },
  );
}

export async function getQualityProfileConfig({ projectId }: GetQualityProfileConfigRequest) {
  return await apiFetch<{ result: any }>(
    null,
    `internal/self_serve/qualityProfile/quality_profile_configs`,
    {
      method: 'GET',
      query: {
        projectId,
      },
    },
  );
}

export async function updateQualityProfileConfig({
  projectId,
  qualityProfileConfig,
}: UpdateQualityProfileConfigRequest) {
  return await apiFetch<{ result: $FIXME }>(
    null,
    `internal/self_serve/qualityProfile/quality_profile_configs`,
    {
      method: 'PUT',
      query: {
        projectId,
      },
      body: {
        qualityProfileConfig,
      },
    },
  );
}

export async function getQualitySignalsByWorkerSkillEntriesAndDateRange(
  body: GetQualitySignalsByWorkerSkillEntryIdsAndDateRangeRequest,
) {
  return await apiFetch<{ result: GetWorkerSkillEntrySignalsResult['workerSkillEntrySignals'] }>(
    null,
    `internal/self_serve/qualityProfile/getWorkerSkillEntrySignalsByWorkerSkillEntriesAndDateRange`,
    {
      method: 'POST',
      body,
    },
  );
}

/** Quality Signals Functions */
export async function getQualitySignals(body: GetQualitySignalsRequest) {
  return await apiFetch<{ result: BulkGetQualitySignalsResult['qualitySignals']; error?: string }>(
    null,
    `internal/self_serve/qualityProfile/getQualitySignals`,
    {
      method: 'POST',
      body,
    },
  );
}

export async function updatePRPConfig(params: $FIXME) {
  return await apiFetch<$FIXME>(null, `internal/self_serve/rlhf/updatePRPConfig`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export async function fetchSpellingAndGrammarEnabled(params: { projectId: string }) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<{ enabled: boolean }>(
    null,
    `internal/self_serve/rlhf/spellingAndGrammarEnabled`,
    { method: 'GET', query },
  );
}

export async function fetchDisablePastingEnabled(params: { projectId: string }) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<{ enabled: boolean }>(
    null,
    `internal/self_serve/rlhf/disablePastingEnabled`,
    { method: 'GET', query },
  );
}

export async function fetchRlhfValidatorConfig(params: { projectId: string }) {
  const query = _.omitBy(params, _.isNil);
  return await apiFetch<IRlhfPromptValidatorConfig>(
    null,
    `internal/self_serve/rlhf/validatorConfig`,
    {
      method: 'GET',
      query,
    },
  );
}

export async function countThreads(params: { project: string; status?: string }) {
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/comment_thread/count`,
    {
      query: { status: params.status as InstructionCommentThreadStatus },
    },
  );
}

export async function fetchAllThreads(params: { project: string }) {
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/comment_thread/get_all_threads`,
  );
}

export async function getModelAnalytics(params: {
  project: string;
  promptLength: string;
  compLength: string;
  batchIds: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelAnalytics/retrieveStats`, {
    method: 'GET',
    query: {
      projectId: params.project,
      promptLength: params.promptLength,
      compLength: params.compLength,
      batchIds: params.batchIds,
    },
  });
}

export async function getTaskPreviewFromMaybeReplication(params: {
  taskId: string;
  runId: string;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/taskPreview`, {
    method: 'POST',
    body: {
      taskId: params.taskId,
      runId: params.runId,
    },
  });
}
export async function getAnalyticsTaskPreview(params: {
  taskId: string;
  getLatestAttempt?: boolean;
}) {
  return await apiFetch(null, `internal/self_serve/modelAnalytics/getTask`, {
    method: 'GET',
    query: {
      taskId: params.taskId,
      getLatestAttempt: params.getLatestAttempt ? params.getLatestAttempt : null,
    },
  });
}

export async function getTaskPreview(params: { taskId: string }) {
  return await apiFetch(null, `internal/customer_app/tasks/${params.taskId}/preview`, {
    method: 'GET',
  });
}

export async function basicEvalRunInfo(params: {
  runId: string;
  workspaceId: string;
}): Promise<ModelEvalRunInfoDTO> {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/evalRunInfo`, {
    method: 'GET',
    query: {
      runId: params.runId,
      workspaceId: params.workspaceId,
    },
  });
}

export async function getRunMetadata(params: { runId: string }) {
  return await apiFetch<ModelEvalRunMetadata | undefined>(
    null,
    `internal/self_serve/modelEvalAnalytics/runMetadata`,
    {
      method: 'GET',
      query: {
        runId: params.runId,
      },
    },
  );
}

export async function getPrimaryMetricScores(params: {
  runId: string;
  modelId: string;
  primaryMetric1: string;
  primaryMetric2?: string;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/primaryMetricScores`, {
    method: 'GET',
    query: {
      runId: params.runId,
      modelId: params.modelId,
      primaryMetric1: params.primaryMetric1,
      ...(params.primaryMetric2 ? { primaryMetric2: params.primaryMetric2 } : {}),
    },
  });
}

export async function getMetricScore(params: { runId: string; modelId: string; metricId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/metricScore`, {
    method: 'GET',
    query: {
      runId: params.runId,
      modelId: params.modelId,
      metricId: params.metricId,
    },
  });
}

export async function getSliceBreakdowns(params: {
  runId: string;
  modelId: string;
  metricId: string;
  metricType: string;
  metadataKeys: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/sliceBreakdowns`, {
    method: 'GET',
    query: {
      runId: params.runId,
      modelId: params.modelId,
      metricId: params.metricId,
      metricType: params.metricType,
      metadataKeys: params.metadataKeys,
    },
  });
}

export async function getAnalysisBreakdown(params: {
  runId: string;
  modelId: string;
  metricId: string;
  metricType: string;
  key: string;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/analysisBreakdown`, {
    method: 'GET',
    query: {
      runId: params.runId,
      modelId: params.modelId,
      metricId: params.metricId,
      metricType: params.metricType,
      key: params.key,
    },
  });
}

export async function getRatingAnalysisBreakdown(params: {
  runId: string;
  modelId: string;
  metricId: string;
  key: string;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/ratingAnalysisBreakdown`, {
    method: 'GET',
    query: {
      runId: params.runId,
      modelId: params.modelId,
      metricId: params.metricId,
      key: params.key,
    },
  });
}

export async function getRankingAnalysisBreakdown(params: {
  runId: string;
  modelId: string;
  metricId: string;
  key: string;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/rankingAnalysisBreakdown`, {
    method: 'GET',
    query: {
      runId: params.runId,
      modelId: params.modelId,
      metricId: params.metricId,
      key: params.key,
    },
  });
}

export async function getLikertAnalysisBreakdown(params: {
  runId: string;
  modelId: string;
  metricId: string;
  key: string;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/likertAnalysisBreakdown`, {
    method: 'GET',
    query: {
      runId: params.runId,
      modelId: params.modelId,
      metricId: params.metricId,
      key: params.key,
    },
  });
}

export async function getAllMetricBreakdownWinRates(params: {
  runId: string;
  modelId: string;
  metricIds: string[];
  key: string;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/allMetricBreakdownWinRates`, {
    method: 'POST',
    body: {
      runId: params.runId,
      modelId: params.modelId,
      metricIds: params.metricIds,
      key: params.key,
    },
  });
}

export async function updateRunSettings(params: {
  runId: string;
  modelId: string;
  metricId: string;
  smallSliceSizeThreshold: number;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/run_settings`, {
    method: 'POST',
    body: {
      runId: params.runId,
      modelId: params.modelId,
      metricId: params.metricId,
      smallSliceSizeThreshold: params.smallSliceSizeThreshold,
    },
  });
}
export async function getAnalysisFieldFilterInfo(params: {
  runId: string;
  metricId: string;
  key: string | undefined;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/analysisFieldFilterInfo`, {
    method: 'GET',
    query: {
      runId: params.runId,
      metricId: params.metricId,
      key: params.key,
    },
  });
}

export async function getHumanEvalTestCasesPreview(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/humanEvalTestCasesPreview`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}
export async function getHumanEvalTestCases(params: { runId: string; taskIds?: string[] }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/humanEvalTestCases`, {
    method: 'GET',
    query: {
      runId: params.runId,
      taskIds: params.taskIds ? JSON.stringify(params.taskIds) : null,
    },
  });
}

export async function getHumanEvalTestCasesMetadata(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/humanEvalTestCasesMetadata`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}

export async function getDeliveryforRun(params: { runId: string }) {
  const response = apiFetch(null, `internal/self_serve/modelEvalAnalytics/deliveryForRun`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
  return response;
}

export async function trackModelEvalEvent(params: any) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/track_event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}
export async function getParsedTestCasePreview(params: { taskId: string; runId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/parsedTestCasePreview`, {
    method: 'GET',
    query: {
      taskId: params.taskId,
      runId: params.runId,
    },
  });
}

export async function getModelEvalIRA(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/getModelEvalIRA`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}

export async function downloadHumanEvalTasks(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/downloadHumanEval`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}

export async function basicBenchmarkRunInfo(params: { runId: string; workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/benchmarkRunInfo`, {
    method: 'GET',
    query: {
      runId: params.runId,
      workspaceId: params.workspaceId,
    },
  });
}

export async function getBenchmarkScore(params: { runId: string; isPublic?: boolean }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/benchmarkScore`, {
    method: 'GET',
    query: {
      runId: params.runId,
      isPublic: params.isPublic,
    },
  });
}

export async function getBenchmarkBreakdown(params: {
  runId: string;
  key: string;
  isPublic?: boolean;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/benchmarkBreakdown`, {
    method: 'GET',
    query: {
      runId: params.runId,
      key: params.key,
      isPublic: params.isPublic,
    },
  });
}

export async function getMetadataKeys(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/metadataKeys`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}

export async function getTestCases(params: {
  runId: string;
  compareRunId?: string;
  benchmark: string;
  isPublic?: boolean;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/testCases`, {
    method: 'GET',
    query: {
      runId: params.runId,
      compareRunId: params.compareRunId,
      benchmark: params.benchmark,
      isPublic: params.isPublic,
    },
  });
}

export async function getBenchmarkLeaderboard(params: {
  privateRuns: string[];
  publicRuns: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/benchmarkLeaderboard`, {
    method: 'GET',
    query: {
      privateRuns: params.privateRuns,
      publicRuns: params.publicRuns,
    },
  });
}

export async function getModelInfo(params: { modelId: string; isPublic?: boolean }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/modelInfo`, {
    method: 'GET',
    query: {
      modelId: params.modelId,
      isPublic: params.isPublic,
    },
  });
}

export async function getBasicRunsInfo(params: { workspaceId: string; pageIndex: number }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/coreRunsInfo`, {
    method: 'GET',
    query: {
      workspaceId: params.workspaceId,
      pageIndex: params.pageIndex,
    },
  });
}

export async function getRunsScores(params: { workspaceId: string; pageIndex: number }) {
  return await apiFetch<EvalCustomerListRunScores>(
    null,
    `internal/self_serve/modelEvalAnalytics/runsScores`,
    {
      method: 'GET',
      query: {
        workspaceId: params.workspaceId,
        pageIndex: params.pageIndex,
      },
    },
  );
}

export async function getRuns(params: { workspaceId: string; pageIndex: number }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/runs`, {
    method: 'GET',
    query: {
      workspaceId: params.workspaceId,
      pageIndex: params.pageIndex,
    },
  });
}

export async function getTestSet(params: { testSetId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/testSets/${params.testSetId}`, {
    method: 'GET',
  });
}

export async function listTestSets(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/testSets`, {
    method: 'GET',
    query: {
      workspaceId: params.workspaceId,
    },
  });
}

export async function getDefaultClusterInfo(params: { modelEvalRunId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/defaultClusterInfo`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
    },
  });
}

export async function getClusterPages(params: { modelEvalRunId: string; clusterType: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/clusterPages`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
      clusterType: params.clusterType,
    },
  });
}

export async function getModelEvalAnalyticsForMetric(params: {
  modelEvalRunId: string;
  metricId: string;
  isRanking: boolean;
  selectedTurns: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/retrieveMetricStats`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
      metricId: params.metricId,
      isRanking: params.isRanking,
      selectedTurns: params.selectedTurns,
    },
  });
}

export async function getModelScoreLists(params: {
  modelEvalRunId: string;
  metricId: string;
  isRanking: boolean;
  selectedTurns: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/metricModelScores`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
      metricId: params.metricId,
      isRanking: params.isRanking,
      selectedTurns: params.selectedTurns,
    },
  });
}

export async function getModelEvalSummaryStats(params: { modelEvalRunId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/summaryStats`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
    },
  });
}

export async function getBreakdownTableKeys(params: { modelEvalRunId: string; metricId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/metricBreakdownTableKeys`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
      metricId: params.metricId,
    },
  });
}

export async function getMetadataKeysFromPrompt(params: { scenarioSetIds: string[] }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/metadataKeysFromPrompt`, {
    method: 'GET',
    query: {
      scenarioSetIds: params.scenarioSetIds,
    },
  });
}

export async function getBreakdownTableData(params: {
  modelEvalRunId: string;
  metricId: string;
  fieldId: string;
  key: string;
  isRanking: boolean;
  selectedTurns: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/metricBreakdownTables`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
      metricId: params.metricId,
      fieldId: params.fieldId,
      key: params.key,
      isRanking: params.isRanking,
      selectedTurns: params.selectedTurns,
    },
  });
}

export async function cancelModelEvalRun(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/cancelRun`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}
export async function getWorkspaceDashboardData(_params: {}) {
  return await apiFetch(null, `internal/self_serve/model_eval/workspaceDashboard`, {
    method: 'GET',
    query: {},
  });
}

export async function getRecentWorkspace(_params: {}) {
  return await apiFetch(null, `internal/self_serve/model_eval/getRecentWorkspace`, {
    method: 'GET',
    query: {},
  });
}

export async function getPromptScorePlotData(params: {
  modelEvalRunId: string;
  metricId: string;
  fieldId: string;
  bucketSize: number;
  isRanking: boolean;
  selectedTurns: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/promptScorePlotData`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
      metricId: params.metricId,
      fieldId: params.fieldId,
      bucketSize: params.bucketSize,
      isRanking: params.isRanking,
      selectedTurns: params.selectedTurns,
    },
  });
}
export async function getInfoForRun(params: { modelEvalRunId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/runInfo`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
    },
  });
}

export async function changeRunName(params: { name: string; runId: string; workspaceId?: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/change_run_name`, {
    method: 'POST',
    body: {
      ...params,
    },
  });
}

export async function changeTestSetName(params: { name: string; testSetId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/testSetName`, {
    method: 'POST',
    body: {
      ...params,
    },
  });
}

export async function getDefaultRun(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/defaultRun`, {
    method: 'GET',
    query: {
      workspaceId: params.workspaceId,
    },
  });
}

export async function getBasicRunInfo(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/basicRunInfo`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}

export async function getClusterData(params: {
  modelEvalRunId: string;
  model1: string;
  model2?: string | null;
  metricId: string;
  sortBy: string;
  sortDirection: string;
  clusterType: string;
  pageIndex: number;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/clusterData`, {
    method: 'GET',
    query: {
      modelEvalRunId: params.modelEvalRunId,
      model1: params.model1,
      model2: params.model2,
      metricId: params.metricId,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
      clusterType: params.clusterType,
      pageIndex: params.pageIndex,
    },
  });
}
export async function getModelEvalRun(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/run`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}

export async function downloadModelEvalTasks(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/downloadTasks`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}
export async function getModelEvalVisualizationLink(params: { runId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/visualization`, {
    method: 'GET',
    query: {
      runId: params.runId,
    },
  });
}

export async function getModelEvalRawData(params: {
  modelEvalRunId: string;
  dataQueryParams?: QueryItem[][];
  pageIndex: number;
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/getRawData`, {
    method: 'POST',
    body: {
      modelEvalRunId: params.modelEvalRunId,
      dataQueryParams: params.dataQueryParams,
      pageIndex: params.pageIndex,
    },
  });
}

export async function getAllModelEvalRuns(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/getAllModelEvalRuns`, {
    method: 'GET',
    query: {
      workspaceId: params.workspaceId,
    },
  });
}
export async function getModelAnalyticsPreferences(params: {
  project: string;
  selectedModel: string;
  promptLength: string;
  compLength: string;
}) {
  return await apiFetch(null, `internal/self_serve/modelAnalytics/retrievePreferences`, {
    method: 'GET',
    query: {
      projectId: params.project,
      model: params.selectedModel,
      promptLength: params.promptLength,
      compLength: params.compLength,
    },
  });
}

export async function getModelAnalyticsRankings(params: {
  project: string;
  batch: string;
  promptLength: string;
  compLength: string;
}) {
  return await apiFetch(null, `internal/self_serve/modelAnalytics/retrieveRankings`, {
    method: 'GET',
    query: {
      projectId: params.project,
      batchId: params.batch,
      promptLength: params.promptLength,
      compLength: params.compLength,
    },
  });
}

export async function getOTSInfo(params: { otsId: string }) {
  return await apiFetch(null, `internal/self_serve/ots/getOtsInfo`, {
    method: 'GET',
    query: {
      otsId: params.otsId,
    },
  });
}

export async function getOTSStats(params: { otsId: string }) {
  return await apiFetch(null, `internal/self_serve/ots/getOtsStats`, {
    method: 'GET',
    query: {
      otsId: params.otsId,
    },
  });
}

export async function suggestOts(params: { suggestion: string }) {
  return await apiFetch(null, `internal/self_serve/ots/suggestOts`, {
    method: 'POST',
    body: {
      suggestion: params.suggestion,
    },
  });
}

export async function notifyInterestInOtsEvals(params: { otsId: string; info: string }) {
  return await apiFetch(null, `internal/self_serve/ots/notify_interest_ots_eval`, {
    method: 'POST',
    body: {
      otsId: params.otsId,
      info: params.info,
    },
  });
}

export async function notifyInterestInOtsTraining(params: { name: string; info: string }) {
  return await apiFetch(null, `internal/self_serve/ots/notify_interest_ots_training`, {
    method: 'POST',
    body: {
      name: params.name,
      info: params.info,
    },
  });
}

export async function getAllOtsInfo(_params: {}) {
  return await apiFetch(null, `internal/self_serve/ots/getAllOtsInfo`, {
    method: 'GET',
    query: {},
  });
}
export async function getModelBatchNames(params: { project: string }) {
  return await apiFetch(null, `internal/self_serve/modelAnalytics/retrieveBatchNames`, {
    method: 'GET',
    query: {
      projectId: params.project,
    },
  });
}

export async function createNewThread(params: {
  project: string;
  message: string;
  instructionComponent?: string | null;
  projectTaxonomy?: string;
}) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/comment_thread/create_new_thread`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function addNewReply(params: { project: string; thread: string; message: string }) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/comment_thread/add_new_reply/${params.thread}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function updateStatus(params: { project: string; thread: string; newStatus: string }) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(
    null,
    `internal/self_serve/project/${params.project}/comment_thread/update_status/${params.thread}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function updateUserProjectGroups(params: { projectGroups: string[] }) {
  const body = _.omitBy(params, _.isNil);
  return await apiFetch(null, `internal/self_serve/update_user_project_groups`, {
    method: 'POST',
    body,
  });
}

export async function fetchSSE(url: string, options: ApiFetchOptions<string>): Promise<void> {
  const isInternalEndpoint = url.startsWith('internal/');
  if (isInternalEndpoint) {
    return await apiFetch(null, url, options);
  } else {
    return await fetchEventSource(url, {
      headers: options.headers,
      signal: options.signal,
      body: options.body,
      onopen: options.fetchEventSourceOptions?.onopen,
      onclose: options.fetchEventSourceOptions?.onclose,
      onmessage: options.fetchEventSourceOptions?.onmessage,
      onerror: options.fetchEventSourceOptions?.onerror,
    });
  }
}

export async function generateUploadToken(params: {
  workspaceId: string;
  importerClientId: string;
}) {
  return await apiFetch(null, `internal/self_serve/assets/token`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function validateScenarioSet(uploadInput: Record<string, any>) {
  return await apiFetch(null, `internal/self_serve/model_eval/scenario_set/validate`, {
    method: 'POST',
    body: uploadInput,
  });
}

export async function validateMultiScenarioSet(uploadInput: Record<string, any>) {
  return await apiFetch(null, `internal/self_serve/model_eval/scenario_set/multi_validate`, {
    method: 'POST',
    body: uploadInput,
  });
}
export async function createScenarioSet(params: any) {
  return await apiFetch(null, `internal/self_serve/model_eval/scenario_set`, {
    method: 'POST',
    body: params,
  });
}

export async function uploadMultiScenarioSetContent(uploadInput: Record<string, any>) {
  return await apiFetch(null, `internal/self_serve/model_eval/scenario_set/multi_upload`, {
    method: 'POST',
    body: uploadInput,
  });
}

export async function uploadScenarioSetContent(uploadInput: Record<string, any>) {
  return await apiFetch(null, `internal/self_serve/model_eval/scenario_set/upload`, {
    method: 'POST',
    body: uploadInput,
  });
}

export async function updateScenarioSet(params: any) {
  return await apiFetch(
    null,
    `internal/self_serve/model_eval/scenario_set/${params.scenarioSetId}`,
    {
      method: 'POST',
      body: params,
    },
  );
}

export async function uploadScenariosContent(uploadInput: Record<string, any>) {
  return await apiFetch(null, `internal/self_serve/model_eval/scenarios/upload`, {
    method: 'POST',
    body: uploadInput,
  });
}
export async function validateEndpointParams(validateInput: Record<string, any>) {
  return await apiFetch(null, `internal/self_serve/model_eval/model_setup/validate`, {
    method: 'POST',
    body: validateInput,
  });
}

export async function validateEndpointMultiParams(validateInput: Record<string, any>) {
  return await apiFetch(null, `internal/self_serve/model_eval/model_setup/multi_validate`, {
    method: 'POST',
    body: validateInput,
  });
}

export async function getEndpointsForRun(params: { workspaceId: string; scenarioSetId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/endpoints_for_run`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function checkIfWorkforceStepNeeded(params: {
  workspaceId: string;
  metricSetId: string;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/check_workforce_needed`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function createSampleTask(params: {
  metricSetId: string;
  scenarioSetId: string;
  endpoints: any[];
  isMulti?: boolean;
  numTurns?: number;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/create_sample_task`, {
    method: 'POST',
    body: {
      ...params,
    },
  });
}

export async function getTags(params: { workspaceId: string; endpointId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/get_tags`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function addTag(params: {
  workspaceId: string;
  endpointId: string;
  tag: string;
  newTag: string;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/add_tag`, {
    method: 'POST',
    body: {
      ...params,
    },
  });
}
export async function createModelEvalEndpoint(params: {
  workspaceId: string;
  name: string;
  url?: string;
  params?: any;
  presetResponsePayload?: {
    scenarioSetId: string;
    presetResponses: {
      reference_id: string;
      content: string[];
    }[];
  };
  systemPrompt?: string;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/model_setup/upload`, {
    method: 'POST',
    body: { ...params },
  });
}

export async function updateModelEvalEndpoint(
  endpointId: string,
  params: {
    workspaceId: string;
    name?: string;
    url?: string;
    params?: any;
    presetResponsePayload?: {
      scenarioSetId: string;
      presetResponses: {
        reference_id: string;
        content: string[];
      }[];
    };
    systemPrompt?: string;
  },
) {
  return await apiFetch(null, `internal/self_serve/model_eval/model_setup/upload/${endpointId}`, {
    method: 'POST',
    body: {
      ...params,
    },
  });
}

export async function updateEndpointWithMulti(params: {
  workspaceId: string;
  endpointId: string;
  scenarioSetId: string;
  presetResponses: {
    reference_id: string;
    content: string[];
  }[];
}) {
  return await apiFetch(
    null,
    `internal/self_serve/model_eval/model_setup/multi_upload/${params.endpointId}`,
    {
      method: 'POST',
      body: {
        workspaceId: params.workspaceId,
        scenarioSetId: params.scenarioSetId,
        presetResponses: params.presetResponses,
        endpointId: params.endpointId,
      },
    },
  );
}
export async function getModelEvalEndpoints(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/endpoints`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function getMetricFromSet(params: { metricSetIds: string[]; workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/metricFromSets`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function getModelEvalHistorySummaryStats(params: {
  selectedModels: string[];
  selectedMetricSets: string[];
  selectedScenarioSets: string[];
  workspaceId: string;
  metricIds: any;
  selectedTags: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/historySummary`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function getHistoryHistogram(params: {
  metricId: string;
  selectedMetricSets: string[];
  selectedModels: string[];
  selectedScenarioSets: string[];
  workspaceId: string;
  selectedTags: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/historyHistogram`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function getHistoryLineGraph(params: {
  metricId: string;
  selectedMetricSets: string[];
  selectedModels: string[];
  selectedScenarioSets: string[];
  workspaceId: string;
  key: string;
  selectedTags: string[];
}) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/historyLineGraph`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function getHistoryInfo(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/modelEvalAnalytics/history`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}
export async function getModelEvalEndpoint(params: { workspaceId: string; endpointId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/endpoint`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function getModelEvalPresets(params: {
  workspaceId: string;
  endpointId: string;
  scenarioSetId: string;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/endpointPresets`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function getModelEvalEndpointInfo(params: {
  workspaceId: string;
  endpointId: string;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/endpointInfo`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function skipModelEvalOnboarding(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/skipOnboarding`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function getModelEvalOnboardingInfo(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/onboardingInfo`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function listScenarioSets(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/scenario_sets`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function listScenarios(params: {
  workspaceId: string;
  limit: number;
  dataQueryParams?: string;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/scenarios`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function archiveScenarioSets(params: {
  workspaceId: string;
  scenarioSetIds: string[];
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/archive_scenario_sets`, {
    method: 'POST',
    body: {
      ...params,
    },
  });
}

export async function changeScenarioSetName(params: { name: string; scenarioSetId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/change_scenario_set_name`, {
    method: 'POST',
    body: {
      ...params,
    },
  });
}

export async function getScenarioSet(params: { workspaceId: string; scenarioSetId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/scenario_set`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function listWorkspaces() {
  return await apiFetch(null, `internal/self_serve/model_eval/workspaces`, {
    method: 'GET',
  });
}

export async function createWorkspace(name: string) {
  return await apiFetch(null, `internal/self_serve/model_eval/workspace`, {
    method: 'POST',
    body: { name },
  });
}

export async function listMetricSets(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/metric_sets`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function listMetrics(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/metrics`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function getMetric(params: { metricId: string; workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/metric`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function createMetric(workspaceId: string, name: string, taxonomy: any) {
  return await apiFetch(null, `internal/self_serve/model_eval/metric`, {
    method: 'POST',
    body: { workspaceId, name, taxonomy },
  });
}
export async function updateMetric(name: string, taxonomy: any, metric: string) {
  return await apiFetch(null, `internal/self_serve/model_eval/metric/${metric}`, {
    method: 'POST',
    body: { name, taxonomy },
  });
}

export async function testAutoMetric(params: {
  prompt: string;
  response: string;
  customPrompt: string;
  customSystemPrompt: string;
  maxVal: number;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/test_auto_metric`, {
    method: 'POST',
    body: params,
  });
}

export async function getAutoEvalRankingSampled(params: {
  prompt: string;
  responseA: string;
  responseB: string;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/auto_eval_ranking_sampled`, {
    method: 'POST',
    body: params,
  });
}

export async function getMetricSet(params: { metricSetId: string; workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/metric_set`, {
    method: 'GET',
    query: {
      ...params,
    },
  });
}

export async function createMetricSet(workspaceId: string, name: string, taxonomy: any) {
  return await apiFetch(null, `internal/self_serve/model_eval/metric_set`, {
    method: 'POST',
    body: { workspaceId, name, taxonomy },
  });
}
export async function updateMetricSet(
  workspaceId: string,
  name: string,
  taxonomy: any,
  metric: string,
) {
  return await apiFetch(null, `internal/self_serve/model_eval/metric_set/${metric}`, {
    method: 'POST',
    body: { workspaceId, name, taxonomy },
  });
}

export async function createEvaluationRun(params: {
  workspaceId: string;
  endpoints: any[]; // string + method
  scenarioSetId: string;
  metricSetId: string;
  workforce: string;
  name: string;
  rapid: boolean;
  studio: boolean;
  type: TaskType;
  createNewProject: boolean;
  project?: string;
  pipeline: TaskPipelineName;
  consensus_attempts: number;
  numTurns: number;
}) {
  return await apiFetch(null, `internal/self_serve/model_eval/run`, {
    method: 'POST',
    body: { ...params },
  });
}

export async function getTaskProps(params: { taskId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/task_props`, {
    method: 'GET',
    query: {
      taskId: params.taskId,
    },
  });
}
export async function getUsersPermissionGroupTaskPermissions(body: {
  userIds: string[];
  permissionGroupId: string;
}) {
  return await apiFetch<{ taskPermissions: IAnyTaskPermissionEntry[] }>(
    null,
    `internal/permissions/list`,
    {
      method: 'POST',
      body,
    },
  );
}

export async function getEGPModelsForUser(params: { workspaceId: string }) {
  return await apiFetch(null, `internal/self_serve/model_eval/get_egp_models_for_user`, {
    method: 'GET',
    query: { workspaceId: params.workspaceId },
  });
}

export async function getTopKChunks(params: {
  query: string;
  relevantChunks: object[];
  rankStrategy?: object;
  topK?: number;
}) {
  const body = _.omitBy(params, _.isNil);
  if (_.isNil(body.rankStrategy)) {
    body.rankStrategy = {
      method: 'cross_encoder',
      params: { cross_encoder_model: 'cross-encoder/ms-marco-MiniLM-L-12-v2' },
    };
  }
  return await apiFetch(null, `internal/self_serve/model_eval/get_top_k_chunks`, {
    method: 'POST',
    body,
  });
}

export async function parsePdfBuffer(body: { buffer: Uint8Array }) {
  return await apiFetch(null, `internal/self_serve/model_eval/parse_pdf_buffer`, {
    method: 'POST',
    body,
  });
}

export const getQualitativeFeedbackForEvalRun = (params: {
  modelEvalRunId: string;
  showArchived?: boolean;
}) =>
  apiFetch<FetchFeedbackForRunResponseDTO>(
    null,
    `/internal/self_serve/qualitativeFeedback/run/${params.modelEvalRunId}`,
    {
      method: 'POST',
      query: {
        ...(params.showArchived ? { showArchived: true } : {}),
      },
    },
  );

export const getQualitativeFeedbackSurveysForEvalRun = (params: {
  modelEvalRunId: string;
  showArchived?: boolean;
}) =>
  apiFetch<ModelQualitativeFeedbackSurveyDTO[]>(
    null,
    `/internal/self_serve/qualitativeFeedback/run/${params.modelEvalRunId}/surveys`,
    {
      method: 'GET',
      query: {
        ...(params.showArchived ? { showArchived: true } : {}),
      },
    },
  );

export const getQualitativeFeedbackSurveyConfig = (params: { modelEvalRunId: string }) =>
  apiFetch<ModelQualitativeFeedbackSurveyConfig | null>(
    null,
    `/internal/self_serve/qualitativeFeedback/surveyConfig/${params.modelEvalRunId}`,
    {
      method: 'GET',
    },
  );

export const archiveQualitativeFeedbackItem = (params: { feedbackId: string }) =>
  apiFetch(null, `/internal/self_serve/qualitativeFeedback/item/${params.feedbackId}`, {
    method: 'PUT',
  });

export const archiveQualitativeSurveyFeedback = (params: { surveyId: string }) =>
  apiFetch(null, `/internal/self_serve/qualitativeFeedback/survey/${params.surveyId}`, {
    method: 'PUT',
  });
